import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';
import useWithGA from '../hooks/useWithGA';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';

import MetatagsForLayout from '../components/MetatagsForLayout';
import useGetCategoryData from '../hooks/data/useGetCategoryData';

import { PAGE_BLOG } from '../constants/strapi';
import { BlogArticle } from '../ui/templates/blogArticle/BlogArticle';
import {
  createUrlPath,
  textToReadingMinutesFormattedText,
} from '../utils/utils';

export const pageQuery = graphql`
  query BlogTemplateData(
    $blogId: Int!
    $nextBlogId: Int
    $pageBuildId: String!
  ) {
    blogData: bwBlog(blogId: { eq: $blogId }) {
      data
    }
    nextBlogData: bwBlog(blogId: { eq: $nextBlogId }) {
      data
    }
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
    bwBlogPageData: bwSinglePage(id: { eq: "page-blog-home" }) {
      data
    }

    bwPageSystemguide: bwSinglePage(id: { eq: "page-systemguide" }) {
      data
    }

    bwPageForVendor: bwSinglePage(id: { eq: "page-for-vendor" }) {
      data
    }
  }
`;

const Template = ({ data, location, pageContext }) => {
  const { getCategoryData } = useGetCategoryData();
  const { trackOutboundLink, savePageView } = useWithGA();
  const { t } = useTranslation();
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;
  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();
  const bwBlogPageData = useTransformGatsbyData(data.bwBlogPageData);
  const blogData = useTransformGatsbyData(data.blogData);
  const nextBlogData = useTransformGatsbyData(data.nextBlogData);
  const bwPageSystemguide = useTransformGatsbyData(data.bwPageSystemguide);
  const bwPageForVendor = useTransformGatsbyData(data.bwPageForVendor);

  const { title, date, cover, category, writer, locale, content } = blogData;
  // const { categoryName } = category;
  const { slug: systemguidePageUrl } = bwPageSystemguide.buildData;
  const { slug: forVendorPageUrl } = bwPageForVendor.buildData;

  const { categoryHasGuide, categoryUrl } = getCategoryData(
    category.categoryDbName
  );
  const categoryGuideUrl = categoryHasGuide
    ? `/${categoryUrl}${systemguidePageUrl}`
    : `${systemguidePageUrl}`;

  const { lastName, firstName } = writer;

  const { slug: blogPageUrl } = bwBlogPageData?.buildData;
  useEffect(() => {
    savePageView({
      type: 'BLOG_ITEM',
    });
  }, []);

  const nextArticleHref = createUrlPath([blogPageUrl, nextBlogData?.slug]);

  const breadcrumbs = [
    {
      title: t('common.home'),
      href: '/',
    },
    {
      title: t('common.blog'),
      href: blogPageUrl,
    },
    {
      title,
    },
  ];
  const sections = [
    {
      sectionType: 'single',
      component: {
        type: 'article',
        componentData: {
          authorImg: null,
          authorName: [firstName, lastName].join(' ').trim(),
          date: new Date(date).toLocaleDateString(locale.replace('_', '-')),
          guest:
            [firstName, lastName].join(' ').trim().toLowerCase() !=
            'Nicolas Zavala'.toLowerCase(), // Gästblogg?
          title,
          /*
          description:
            'TODO Konsultföretag och andra aktörer i professional services-branschen är proffs på att hjälpa sina klienter lösa svårigheter genom handfast vägledning och rådgivning. Men de står också inför egna utmaningar.',
          categories: ['TODO Lönehantering', 'HR'],
          */
          /* duration:textToReadingMinutesFormattedText({
              text: content,
              nearest: 5,
              t,
            })
            */
          articleImg: blogData?.cover?.formats?.medium?.url
            ? blogData?.cover?.formats?.medium?.url
            : '',
          articleImgCaption: blogData?.cover?.caption
            ? blogData?.cover?.caption
            : '',
          /*
            introText:
            'TODO I Delteks senaste årliga branschstudie ‘EMEA and APAC Deltek Clarity’ tillfrågades hundratals konsultföretag om deras mål och utmaningar. Framför allt var respondenterna bekymrade över att vinna rätt upphandlingsprocesser och lönsamma projekt, attrahera och behålla den rätta kompetensen samt hantera resurser på bästa sätt för att leva upp till sina klienters förväntningar. Många företag hade även ännu inte implementerat de allra nödvändigaste nyckeltalen (KPI:er), för att kunna följa sin utveckling mot att nå sina mål. Nedan finner du ett axplock på de mest användbara KPI:erna för just konsultföretag, så läs vidare för att se hur du kan mäta framgång på bästa sätt, och upptäck varför det lönar sig att centralisera KPI-hanteringen på en enda platform som är byggd för just din bransch.',
            */
          content: blogData?.content || null,
          categoryGuideUrl,
        },
      },
    },

    {
      sectionType: 'single',
      component: {
        type: 'helperData',
        title: t('formContactUs.contactUs'),
        subtitle: '',
        button1Label: 'info@businesswith.com',
        button1Href: 'mailto:info@businesswith.com',
        button2Label: t('common.forVendors'),
        button2Href: forVendorPageUrl,
      },
    },
    nextBlogData.id !== blogData.id && {
      sectionType: 'single',
      component: {
        type: 'nextArticle',
        componentData: {
          authorImg: null,
          authorName: null,
          date: new Date(nextBlogData.date).toLocaleDateString(
            locale.replace('_', '-')
          ), // '2024 03 04',
          guest: true,
          title: nextBlogData?.title,
          categories: [nextBlogData?.category?.categoryName],
          // duration: 'TODO 5 min lästid',
          articleImg: nextBlogData?.cover?.formats?.small?.url,
          articleImgCaption: nextBlogData?.title,
          introText: nextBlogData?.excerpt || null,
          href: nextArticleHref,
        },
      },
    },
  ];

  // prettier json output from articleArgs
  return BlogArticle({
    breadcrumbs,
    footer,
    siteUrls,
    menu,
    location,
    search,
    sections,
    t,
  });
};
export default Template;

export function Head({ data, location, pageContext }) {
  const { locale } = pageContext;

  const blogData = useTransformGatsbyData(data.blogData);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;
  const { seo } = blogData;

  /*
  let imageData;
  if (cover?.localFile) {
    imageData = getImage(cover.localFile);
  }
  */

  return (
    <MetatagsForLayout
      title={seo?.metaTitle}
      description={seo?.metaDescription}
      pathname={location.pathname}
      locale={locale}
      siteUrls={siteUrls}
      // thumbnail={imageData}
    />
  );
}
